'use client';
import { useTranslation } from '@/i18n/client';
import { Dialog, DialogTitle } from '@mui/material';
import style from './index.module.scss';
import clsx from 'clsx';
import type { Variant } from '@/intl-jdi-common-ssr/types/checkout';

interface UKDialogType {
  open: boolean;
  title?: string | React.ReactElement;
  children: React.ReactNode;
  customButtonTextFontSize?: number;
  customButtonText?: string;
  cancelButtonText?: string; // 取消按钮文本
  isBackdrop?: boolean; // 是否允许点击蒙层关闭，默认true
  handOnlyClose?: () => void; // 仅关闭弹窗
  handleClose: (e?: React.MouseEvent) => void; // 关闭弹窗（改为必选）
  handleCancel?: (e?: React.MouseEvent) => void; // 取消按钮回调
  sx?: any;
  isShowBtnBox?: boolean; // 是否显示底部按钮
  buttonLayout?: 'horizontal' | 'vertical'; // 保留按钮布局选项
  btnStyle?: React.CSSProperties; // 新增按钮容器样式
  cancelBtnStyle?: React.CSSProperties; //取消按钮的样式
  textContainerStyle?: React.CSSProperties; //主内容的样式
  actionBtns?: {
    text: string;
    variant?: Variant;
    onClick: () => void;
  }[];
}

const UKDialog = ({
  open,
  title,
  children,
  handOnlyClose,
  handleClose,
  customButtonTextFontSize,
  customButtonText,
  cancelButtonText,
  handleCancel,
  sx,
  isBackdrop = true,
  isShowBtnBox = true,
  buttonLayout = 'horizontal', // 保留布局默认值
  btnStyle, // 新增样式属性
  cancelBtnStyle,
  textContainerStyle,
  actionBtns,
}: UKDialogType) => {
  // 保留按钮类名动态计算逻辑
  const getButtonClassName = () => {
    if (buttonLayout === 'vertical') {
      return cancelButtonText ? style.verticalButtonDialogButton : style.verticalSingleButton;
    }
    return cancelButtonText ? style.twoButtonDialogButton : style.dialogButton;
  };

  const buttonClassName = getButtonClassName();
  const { t } = useTranslation();

  return (
    <>
      <Dialog
        open={open}
        onClose={(e: any, reason: any) => {
          if (reason === 'backdropClick' && !isBackdrop) {
            return;
          }
          (handOnlyClose ?? handleCancel ?? handleClose)?.(e);
        }}
        sx={{ ...DialogContainerStyles, ...sx }}
      >
        <div className={style.textContainer} style={textContainerStyle}>
          {title && <DialogTitle sx={DialogTitleStyles}>{title}</DialogTitle>}
          {children}
        </div>

        {isShowBtnBox && actionBtns?.length ? (
          <div className={style.actions}>
            {actionBtns.map((item) => {
              return (
                <button
                  className={clsx(style[item?.variant || 'contained'])}
                  key={item?.text}
                  onClick={item?.onClick}
                >
                  {item?.text}
                </button>
              );
            })}
          </div>
        ) : (
          <div
            className={
              buttonLayout === 'vertical' ? style.verticalButtonContainer : style.buttonContainer
            }
            style={btnStyle} // 应用自定义样式
          >
            {cancelButtonText && (
              <button
                onClick={(e) => (handleCancel ? handleCancel() : handleClose(e))}
                className={buttonClassName}
                style={{
                  fontSize: customButtonTextFontSize ? `${customButtonTextFontSize}px` : '18px',
                  ...cancelBtnStyle,
                }}
              >
                {cancelButtonText}
              </button>
            )}
            <button
              onClick={(e) => handleClose(e)}
              className={buttonClassName}
              style={{
                fontSize: customButtonTextFontSize ? `${customButtonTextFontSize}px` : '18px',
              }}
            >
              {customButtonText ? customButtonText : t('common.btnText.ok')}
            </button>
          </div>
        )}
      </Dialog>
    </>
  );
};

const DialogTitleStyles = {
  fontSize: '16px',
  lineHeight: '20px',
  padding: 0,
  marginBottom: '8px',
};

const DialogContainerStyles = {
  '& .MuiDialog-container': {
    '& .MuiPaper-root': {
      width: '295px',
      display: 'flex',
      flexDirection: 'column',
      borderRadius: '16px',
    },
  },
};

export default UKDialog;
